// Common field interfaces
interface ImageData {
  id?: string;
  image: string;
  placeholder?: string;
  isThumbnail: boolean;
}

// Products interfaces
interface ProductAttribute {
  attributeId: string;
  values: string[];
}

interface ProductPayload {
  productId?: string; // Optional, only for updates
  slug: string;
  productName: string;
  productDescription: string;
  brandId: string;
  published: boolean;
  images: ImageData[];
  tags: string[];
  categories: string[];
  attributes: ProductAttribute[];
}

interface RawProductData {
  productId?: string;
  slug?: string;
  productName?: string;
  productDescription?: string;
  brandId?: string;
  published?: boolean;
  images?: any[];
  tags?: any[];
  categories?: any[];
  attributes?: any[];
  [key: string]: any; // Allow for additional properties
}

// Variant interfaces
interface VariantAttribute {
  productAttributeId: string;
  productAttributeValue: string;
}

interface VariantTag {
  tagName: string;
  id?: string;
}

interface VariantPayload {
  variantId?: string; // Optional, only for updates
  sellerId: string;
  productId: string;
  salePrice: number;
  originalPrice: number;
  buyingPrice: number;
  availableUnits: number;
  states: string[];
  images: ImageData[];
  publish: boolean;
  active: boolean;
  tags: VariantTag[];
  additionalInfo: string[];
  attributes: VariantAttribute[];
}

interface RawVariantData {
  variantId?: string;
  sellerId?: string;
  productId?: string;
  salePrice?: string | number;
  originalPrice?: string | number;
  buyingPrice?: string | number;
  availableUnits?: string | number;
  quantity?: string | number; // Alternative field name
  states?: any[];
  images?: any[];
  publish?: boolean;
  active?: boolean;
  tags?: any[];
  additionalInfo?: any[];
  attributes?: any[];
  [key: string]: any; // Allow for additional properties
}

// Type for any raw data
type RawData = RawProductData | RawVariantData;

// Main formatter function that handles both products and variants
export const formatPayload = (
  rawData: RawData,
  isUpdate: boolean,
  isProduct: boolean,
  baseUrl: string
): ProductPayload | VariantPayload => {
  // Determine which type of entity we're working with
  if (isProduct) {
    return formatProductPayload(rawData as RawProductData, isUpdate, baseUrl);
  } else {
    return formatVariantPayload(rawData as RawVariantData, isUpdate, baseUrl);
  }
};


export const toDateTime = (date: string | number | Date): string => {
  const dateTime = new Date(date);
  // Format the date to ISO string for clear representation
  // This will convert the date to UTC format
  // and return it in the format YYYY-MM-DD HH:mm:ss.sssZ
  return dateTime.toISOString().replace('T', ' ').slice(0, 16);
}
// Products formatter
function formatProductPayload(
  rawData: RawProductData,
  isUpdate: boolean,
  baseUrl: string
): ProductPayload {
  // Extract ID from URL if it's an update
  let productId: string | null = null;
  if (isUpdate) {
    const idMatch = baseUrl.match(/\/products\/([^\/]+)$/);
    productId = idMatch ? idMatch[1] : rawData.productId || null;
  }

  // Create the formatted payload
  const payload: ProductPayload = {
    slug: rawData.slug || createSlugFromName(rawData.productName || ''),
    productName: rawData.productName || '',
    productDescription: rawData.productDescription || '',
    brandId: rawData.brandId || '',
    published: rawData.published !== undefined ? rawData.published : true,
    images: formatImages(rawData.images),
    tags: formatProductTags(rawData.tags),
    categories: formatCategories(rawData.categories),
    attributes: formatProductAttributes(rawData.attributes)
  };

  // Add ID for updates
  if (isUpdate && productId) {
    payload.productId = productId;
  }

  return payload;
}

// Variant formatter
function formatVariantPayload(
  rawData: RawVariantData,
  isUpdate: boolean,
  baseUrl: string
): VariantPayload {
  // Extract ID from URL if it's an update
  let variantId: string | null = null;
  if (isUpdate) {
    const idMatch = baseUrl.match(/\/variants\/([^\/]+)$/);
    variantId = idMatch ? idMatch[1] : rawData.variantId || null;
  }

  // Create the formatted payload
  const payload: VariantPayload = {
    sellerId: rawData.sellerId || '',
    productId: rawData.productId || '',
    salePrice: parseFloat(String(rawData.salePrice)) || 0,
    originalPrice:
      parseFloat(String(rawData.originalPrice || rawData.salePrice)) || 0,
    buyingPrice: parseFloat(String(rawData.buyingPrice)) || 0,
    availableUnits:
      parseInt(String(rawData.availableUnits || rawData.quantity)) || 0,
    states: formatVariantStates(rawData.states),
    images: formatImages(rawData.images),
    publish: rawData.publish !== undefined ? rawData.publish : true,
    active: rawData.active !== undefined ? rawData.active : true,
    tags: formatVariantTags(rawData.tags),
    additionalInfo: formatAdditionalInfo(rawData.additionalInfo),
    attributes: formatVariantAttributes(rawData.attributes)
  };

  // Add ID for updates
  if (isUpdate && variantId) {
    payload.variantId = variantId;
  }

  return payload;
}

// HELPER FUNCTIONS

// Helper to create slug from product name
function createSlugFromName(productName: string): string {
  return productName
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
}

// Helper to format images
function formatImages(images: any[] | undefined): ImageData[] {
  if (!images || !Array.isArray(images)) return [];

  return images.map((img) => ({
    id: img.id,
    image: img.image || '',
    placeholder: img.placeholder,
    isThumbnail: img.isThumbnail !== undefined ? img.isThumbnail : false
  }));
}

// Helper to format product tags (array of string IDs)
function formatProductTags(tags: any[] | undefined): string[] {
  if (!tags || !Array.isArray(tags)) return [];

  // If tags is already an array of strings (IDs), return as is
  if (tags.length > 0 && typeof tags[0] === 'string') {
    return tags;
  }

  // If tags is an array of objects
  if (tags.length > 0 && typeof tags[0] === 'object') {
    return tags.map((tag) => tag.id || tag.value || tag.tagName || '');
  }

  return [];
}

// Helper to format variant tags (array of objects with tagName)
function formatVariantTags(tags: any[] | undefined): VariantTag[] {
  if (!tags || !Array.isArray(tags)) return [];

  // If tags is already in the correct format
  if (
    tags.length > 0 &&
    typeof tags[0] === 'object' &&
    tags[0].tagName !== undefined
  ) {
    return tags as VariantTag[];
  }

  // Convert to proper format
  return tags.map((tag) => {
    if (typeof tag === 'string') {
      return { tagName: tag };
    } else if (typeof tag === 'object') {
      return {
        tagName:
          tag.tagName || tag.name || tag.label || tag.id || tag.value || '',
        id: tag.id
      };
    }
    return { tagName: String(tag) };
  });
}

// Helper to format categories
function formatCategories(categories: any[] | undefined): string[] {
  if (!categories || !Array.isArray(categories)) return [];

  // If categories is already an array of strings (IDs), return as is
  if (categories.length > 0 && typeof categories[0] === 'string') {
    return categories;
  }

  // If categories is an array of objects with id or value
  if (categories.length > 0 && typeof categories[0] === 'object') {
    return categories.map((category) => category.id || category.value || '');
  }

  return [];
}

// Helper to format variant states
function formatVariantStates(states: any[] | undefined): string[] {
  if (!states || !Array.isArray(states)) return [];

  // If states is already an array of strings (IDs), return as is
  if (states.length > 0 && typeof states[0] === 'string') {
    return states;
  }

  // If states is an array of objects
  if (states.length > 0 && typeof states[0] === 'object') {
    return states.map((state) => state.id || state.value || '');
  }

  return [];
}

// Helper to format additional info
function formatAdditionalInfo(additionalInfo: any[] | undefined): string[] {
  if (!additionalInfo || !Array.isArray(additionalInfo)) return [];

  return additionalInfo.map((info) =>
    typeof info === 'string' ? info : String(info)
  );
}

// Helper to format product attributes
function formatProductAttributes(
  attributes: any[] | undefined
): ProductAttribute[] {
  if (!attributes || !Array.isArray(attributes)) return [];

  // Check if attributes is already in the correct format
  if (
    attributes.length > 0 &&
    attributes[0] &&
    attributes[0].attributeId &&
    attributes[0].values
  ) {
    return attributes as ProductAttribute[];
  }

  // Convert to proper format
  return attributes.map((attr) => {
    // Handle different possible attribute formats
    if (attr.attribute && attr.value) {
      return {
        attributeId: attr.attribute.id || attr.attributeId || '',
        values: Array.isArray(attr.value) ? attr.value : [attr.value]
      };
    } else if (attr.attributeId) {
      return {
        attributeId: attr.attributeId,
        values: Array.isArray(attr.values)
          ? attr.values
          : Array.isArray(attr.value)
          ? attr.value
          : [attr.value || attr.values].filter(Boolean)
      };
    }

    // Fallback with empty values to prevent errors
    return {
      attributeId: attr.attributeId || attr.id || '',
      values: []
    };
  });
}

// Helper to format variant attributes
function formatVariantAttributes(
  attributes: any[] | undefined
): VariantAttribute[] {
  if (!attributes || !Array.isArray(attributes)) return [];

  // Check if attributes is already in the correct format
  if (
    attributes.length > 0 &&
    attributes[0] &&
    attributes[0].productAttributeId &&
    attributes[0].productAttributeValue
  ) {
    return attributes as VariantAttribute[];
  }

  // Convert to proper format
  return attributes.map((attr) => {
    // Handle different possible attribute formats
    if (attr.attribute && attr.value) {
      return {
        productAttributeId:
          attr.attribute.id ||
          attr.attributeId ||
          attr.productAttributeId ||
          '',
        productAttributeValue: attr.value || ''
      };
    } else if (attr.productAttributeId && attr.values) {
      return {
        productAttributeId: attr.productAttributeId,
        productAttributeValue: Array.isArray(attr.values)
          ? attr.values[0]
          : attr.values
      };
    } else if (attr.productAttributeId && attr.productAttributeValueId) {
      return {
        productAttributeId: attr.productAttributeId,
        productAttributeValue: attr.productAttributeValueId
      };
    }

    // Fallback with empty values to prevent errors
    return {
      productAttributeId: attr.productAttributeId || attr.attributeId || '',
      productAttributeValue: attr.productAttributeValue || attr.value || ''
    };
  });
}
